import React, { useRef } from 'react';
import { Cropper, ReactCropperElement } from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { Box, Button, Typography } from '@mui/material';
import type { StagedFile } from '@securspace/securspace-ui-kit';
import { useCropperRemountOnResize } from './useCropperRemountOnResize';
import { useCropConfirm } from './useCropConfirm';
import {
  getCropActionBarSx,
  getCropErrorSx,
  getCropPanelRootSx,
  getCropStageSx,
} from './cropPanelStyles';

export interface CropPanelProps {
  /** Staged file to crop. `file.previewUrl` is used as the crop source. */
  file: StagedFile;
  /** Fired on a successful crop with a new blob URL set as `previewUrl`. */
  onComplete: (updated: StagedFile) => void;
  /** Fired when the user cancels. No blob URL is generated and no state changes. */
  onCancel: () => void;
  /** Optional. Called with a user-facing message if `canvas.toBlob()` returns null. */
  onError?: (message: string) => void;
  /** True when hosted in `ImagePreviewModal`'s full-screen mode: the cropper fills the available viewport height (iOS Photos-style) instead of a fixed small preview height. */
  fullScreen?: boolean;
}

/**
 * Self-contained crop interface. Owns the display → region select → confirm
 * lifecycle; the confirm/blob-conversion state machine is delegated to
 * `useCropConfirm` and the resize-remount workaround to
 * `useCropperRemountOnResize`, keeping this component purely presentational.
 *
 * Does not revoke any blob URLs — that is the caller's responsibility.
 */
const CropPanel = ({ file, onComplete, onCancel, onError, fullScreen }: CropPanelProps) => {
  const cropperRef = useRef<ReactCropperElement>(null);
  const cropperInstanceKey = useCropperRemountOnResize();
  const { confirming, error, handleConfirm } = useCropConfirm(file, onComplete, onError);

  return (
    <Box sx={getCropPanelRootSx(Boolean(fullScreen))}>
      <Box sx={getCropStageSx(Boolean(fullScreen))}>
        <Cropper
          key={cropperInstanceKey}
          ref={cropperRef}
          src={file.previewUrl}
          guides
          viewMode={1}
          rotatable
          checkOrientation={false}
          autoCropArea={1}
          // Disabled in favor of the debounced full-remount from useCropperRemountOnResize.
          responsive={false}
          style={{ width: '100%', height: '100%' }}
        />
      </Box>

      {error && (
        <Typography
          role="alert"
          color="error"
          variant="body2"
          sx={getCropErrorSx(Boolean(fullScreen))}
        >
          {error}
        </Typography>
      )}

      <Box sx={getCropActionBarSx(Boolean(fullScreen))}>
        <Button onClick={onCancel} color="inherit" disabled={confirming}>
          Cancel
        </Button>
        <Button
          onClick={() => handleConfirm(cropperRef.current?.cropper)}
          disabled={confirming}
          variant="contained"
        >
          {confirming ? 'Cropping…' : 'Confirm'}
        </Button>
      </Box>
    </Box>
  );
};

export default CropPanel;


